import React, { useState, useEffect } from 'react';
import {
  Building, Users, Database, Plus, Edit2, Trash2, CheckCircle2,
  XCircle, RefreshCw, Server, Shield, Key, Eye, EyeOff, Check,
  AlertTriangle, ArrowRight, Zap, Play, Layers
} from 'lucide-react';

export default function AdminTenantsPage({ token, currentImpersonateId, onSwitchTenant }) {
  const [activeTab, setActiveTab] = useState('tenants'); // 'tenants' | 'users'
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actionStatus, setActionStatus] = useState(null);

  // Modal State for Tenant
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [deletingTenant, setDeletingTenant] = useState(null);
  const [dropDbOption, setDropDbOption] = useState(false);
  const [isDeletingTenant, setIsDeletingTenant] = useState(false);
  const [tenantForm, setTenantForm] = useState({
    name: '',
    slug: '',
    db_type: 'postgres',
    host: 'localhost',
    port: 5432,
    database_name: 'loops',
    username: 'postgres',
    password: '',
    ssl: false,
    custom_connection_string: '',
    auto_provision: true,
    initial_user_email: '',
    initial_user_password: ''
  });

  // Modal State for User
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    tenant_id: '',
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'user'
  });

  const [testingDbId, setTestingDbId] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [provisioningId, setProvisioningId] = useState(null);

  const getAuthHeaders = () => {
    const effectiveToken = token || localStorage.getItem('loops_auth_token');
    return effectiveToken ? { Authorization: `Bearer ${effectiveToken}` } : {};
  };

  const fetchAdminData = async () => {
    setIsLoading(true);
    const authHeaders = getAuthHeaders();
    if (!authHeaders.Authorization) {
      setActionStatus({
        type: 'error',
        isAuthError: true,
        message: 'Non authentifié. Veuillez vous reconnecter en tant qu\'administrateur.'
      });
      setIsLoading(false);
      return;
    }

    try {
      const [tenantsRes, usersRes] = await Promise.all([
        fetch('/api/admin/tenants', { headers: authHeaders }),
        fetch('/api/admin/users', { headers: authHeaders })
      ]);

      if (tenantsRes.status === 401 || usersRes.status === 401) {
        setActionStatus({
          type: 'error',
          isAuthError: true,
          message: 'Session expirée ou non autorisée (401). Veuillez vous reconnecter en tant qu\'administrateur.'
        });
        return;
      }

      if (tenantsRes.ok) {
        const tData = await tenantsRes.json();
        setTenants(tData.tenants || []);
      } else {
        const err = await tenantsRes.json().catch(() => ({}));
        setActionStatus({ type: 'error', message: err.error || `Erreur chargement organisations (${tenantsRes.status})` });
      }

      if (usersRes.ok) {
        const uData = await usersRes.json();
        setUsers(uData.users || []);
      }
    } catch (err) {
      console.error('Error fetching admin data:', err);
      setActionStatus({ type: 'error', message: `Erreur réseau : ${err.message}` });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminData();
  }, [token]);

  // Test DB Connection for an existing tenant
  const handleTestTenantDb = async (tenant) => {
    setTestingDbId(tenant.id);
    try {
      const res = await fetch(`/api/admin/tenants/${tenant.id}/test-db`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setTestResults(prev => ({
        ...prev,
        [tenant.id]: data
      }));
    } catch (err) {
      setTestResults(prev => ({
        ...prev,
        [tenant.id]: { success: false, message: err.message }
      }));
    } finally {
      setTestingDbId(null);
    }
  };

  // Provision / Upgrade Schema for a tenant
  const handleProvisionSchema = async (tenantId) => {
    if (!confirm('Exécuter le schéma et initialiser les tables pour cette base de données ?')) return;
    setProvisioningId(tenantId);
    try {
      const res = await fetch(`/api/admin/tenants/${tenantId}/provision-schema`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setActionStatus({ type: 'success', message: data.message });
      } else {
        setActionStatus({ type: 'error', message: data.error || 'Échec de l\'initialisation du schéma' });
      }
    } catch (err) {
      setActionStatus({ type: 'error', message: err.message });
    } finally {
      setProvisioningId(null);
    }
  };

  // Save Tenant (Create or Update)
  const handleSaveTenant = async (e) => {
    e.preventDefault();
    const headers = {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    };
    try {
      if (editingTenant) {
        const res = await fetch(`/api/admin/tenants/${editingTenant.id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(tenantForm)
        });
        const data = await res.json();
        if (res.ok && data.success) {
          setActionStatus({ type: 'success', message: data.message });
          setShowTenantModal(false);
          setEditingTenant(null);
          await fetchAdminData();
        } else {
          setActionStatus({ type: 'error', message: data.error || 'Erreur lors de la mise à jour' });
        }
      } else {
        const res = await fetch('/api/admin/tenants', {
          method: 'POST',
          headers,
          body: JSON.stringify(tenantForm)
        });
        const data = await res.json();
        if (res.ok && data.success) {
          setActionStatus({ type: 'success', message: data.message });
          setShowTenantModal(false);
          setEditingTenant(null);
          await fetchAdminData();
        } else {
          setActionStatus({ type: 'error', message: data.error || 'Erreur lors de la création de l\'organisation' });
        }
      }
    } catch (err) {
      setActionStatus({ type: 'error', message: err.message });
    }
  };

  // Delete Tenant Confirmation
  const handleConfirmDeleteTenant = async () => {
    if (!deletingTenant) return;
    setIsDeletingTenant(true);
    try {
      const res = await fetch(`/api/admin/tenants/${deletingTenant.id}?dropDatabase=${dropDbOption ? 'true' : 'false'}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setActionStatus({ type: 'success', message: data.message });
        setDeletingTenant(null);
        await fetchAdminData();
      } else {
        setActionStatus({ type: 'error', message: data.error || 'Erreur lors de la suppression' });
      }
    } catch (err) {
      setActionStatus({ type: 'error', message: err.message });
    } finally {
      setIsDeletingTenant(false);
    }
  };

  // Save User (Create or Update)
  const handleSaveUser = async (e) => {
    e.preventDefault();
    const headers = {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    };
    try {
      const payload = {
        ...userForm,
        tenant_id: userForm.tenant_id ? parseInt(userForm.tenant_id) : (tenants[0]?.id || 1)
      };

      if (editingUser) {
        const res = await fetch(`/api/admin/users/${editingUser.id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (res.ok && data.success) {
          setActionStatus({ type: 'success', message: data.message });
          setShowUserModal(false);
          setEditingUser(null);
          await fetchAdminData();
        } else {
          setActionStatus({ type: 'error', message: data.error || 'Erreur lors de la modification' });
        }
      } else {
        const res = await fetch('/api/admin/users', {
          method: 'POST',
          headers,
          body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (res.ok && data.success) {
          setActionStatus({ type: 'success', message: data.message });
          setShowUserModal(false);
          setEditingUser(null);
          await fetchAdminData();
        } else {
          setActionStatus({ type: 'error', message: data.error || 'Erreur lors de la création de l\'utilisateur' });
        }
      }
    } catch (err) {
      setActionStatus({ type: 'error', message: err.message });
    }
  };

  // Delete User
  const handleDeleteUser = async (id, email) => {
    if (!confirm(`Supprimer l'utilisateur '${email}' ?`)) return;
    try {
      const res = await fetch(`/api/admin/users/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setActionStatus({ type: 'success', message: data.message });
        await fetchAdminData();
      } else {
        setActionStatus({ type: 'error', message: data.error || 'Erreur lors de la suppression' });
      }
    } catch (err) {
      setActionStatus({ type: 'error', message: err.message });
    }
  };

  return (
    <div className="database-container" style={{ background: '#0f172a', padding: '16px 24px', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      
      {/* 1. TOP SUB-NAVBAR */}
      <div className="database-sub-navbar">
        <div className="db-sub-left">
          <div className="db-sub-tabs">
            <button
              className={`db-sub-tab ${activeTab === 'tenants' ? 'active' : ''}`}
              onClick={() => setActiveTab('tenants')}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <Building size={15} />
              <span>Organisations & Bases de données ({tenants.length})</span>
            </button>

            <button
              className={`db-sub-tab ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => setActiveTab('users')}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <Users size={15} />
              <span>Comptes Utilisateurs ({users.length})</span>
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {activeTab === 'tenants' && (
            <button
              className="btn-primary"
              onClick={() => {
                setEditingTenant(null);
                setTenantForm({
                  name: '',
                  slug: '',
                  db_type: 'postgres',
                  host: 'localhost',
                  port: 5432,
                  database_name: 'loops_client',
                  username: 'postgres',
                  password: '',
                  ssl: false,
                  custom_connection_string: '',
                  auto_provision: true,
                  initial_user_email: '',
                  initial_user_password: ''
                });
                setShowTenantModal(true);
              }}
              style={{ padding: '6px 14px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <Plus size={15} />
              <span>Ajouter une Organisation</span>
            </button>
          )}

          {activeTab === 'users' && (
            <button
              className="btn-primary"
              onClick={() => {
                setEditingUser(null);
                setUserForm({
                  tenant_id: tenants[0]?.id || '',
                  email: '',
                  password: '',
                  first_name: '',
                  last_name: '',
                  role: 'user'
                });
                setShowUserModal(true);
              }}
              style={{ padding: '6px 14px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <Plus size={15} />
              <span>Créer un Utilisateur</span>
            </button>
          )}

          <button
            onClick={fetchAdminData}
            disabled={isLoading}
            style={{
              background: '#1e293b',
              border: '1px solid #334155',
              color: '#f8fafc',
              borderRadius: '6px',
              padding: '6px 12px',
              fontSize: '0.78rem',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <RefreshCw size={13} style={{ animation: isLoading ? 'spin 1s linear infinite' : 'none' }} />
            <span>Actualiser</span>
          </button>
        </div>
      </div>

      {/* Global Action Status Alert */}
      {actionStatus && (
        <div style={{
          background: actionStatus.type === 'success' ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
          border: `1px solid ${actionStatus.type === 'success' ? '#22c55e' : '#ef4444'}`,
          color: actionStatus.type === 'success' ? '#4ade80' : '#fca5a5',
          padding: '10px 16px',
          borderRadius: '8px',
          fontSize: '0.82rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {actionStatus.type === 'success' ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
            <span>{actionStatus.message}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {actionStatus.isAuthError && (
              <button
                onClick={() => {
                  localStorage.removeItem('loops_auth_token');
                  localStorage.removeItem('loops_user_data');
                  window.location.reload();
                }}
                style={{
                  background: '#ef4444',
                  color: '#ffffff',
                  border: 'none',
                  padding: '4px 10px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '0.78rem'
                }}
              >
                Se reconnecter
              </button>
            )}
            <button
              onClick={() => setActionStatus(null)}
              style={{ background: 'none', border: 'none', color: '#cbd5e1', cursor: 'pointer', fontSize: '0.75rem' }}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 2. TAB 1: ORGANISATIONS & POSTGRESQL DATABASES */}
      {/* ========================================================================= */}
      {activeTab === 'tenants' && (
        <div className="database-view-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px', overflowY: 'auto', paddingRight: '4px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: '16px' }}>
            {tenants.map(tenant => {
              const isCurrent = (currentImpersonateId || 1) === tenant.id;
              const testRes = testResults[tenant.id];

              return (
                <div
                  key={tenant.id}
                  style={{
                    background: '#1e293b',
                    border: isCurrent ? '1px solid #38bdf8' : '1px solid #334155',
                    borderRadius: '12px',
                    padding: '18px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '14px',
                    boxShadow: isCurrent ? '0 0 20px rgba(56, 189, 248, 0.15)' : 'none'
                  }}
                >
                  {/* Card Header */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        width: '38px',
                        height: '38px',
                        borderRadius: '10px',
                        background: tenant.id === 1 ? 'linear-gradient(135deg, #0284c7 0%, #38bdf8 100%)' : 'rgba(56, 189, 248, 0.15)',
                        color: tenant.id === 1 ? '#0f172a' : '#38bdf8',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '800'
                      }}>
                        <Building size={20} />
                      </div>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '1.05rem', fontWeight: '800', color: '#f8fafc' }}>
                            {tenant.name}
                          </span>
                          {tenant.id === 1 && (
                            <span style={{ background: '#38bdf8', color: '#0f172a', fontSize: '0.65rem', fontWeight: '900', padding: '1px 6px', borderRadius: '4px' }}>
                              DEFAULT
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '2px' }}>
                          Slug: <code style={{ color: '#38bdf8' }}>{tenant.slug}</code> • {tenant.users_count} utilisateur(s)
                        </div>
                      </div>
                    </div>

                    {/* Impersonate / Switch Button */}
                    <button
                      onClick={() => onSwitchTenant && onSwitchTenant(tenant.id, tenant.name)}
                      style={{
                        background: isCurrent ? 'rgba(34, 197, 94, 0.2)' : 'rgba(56, 189, 248, 0.12)',
                        color: isCurrent ? '#22c55e' : '#38bdf8',
                        border: `1px solid ${isCurrent ? '#22c55e' : 'rgba(56, 189, 248, 0.3)'}`,
                        borderRadius: '6px',
                        padding: '4px 10px',
                        fontSize: '0.72rem',
                        fontWeight: '800',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                      title="Basculer vers les données de cette organisation"
                    >
                      {isCurrent ? <Check size={13} /> : <Play size={13} />}
                      <span>{isCurrent ? 'Actif' : 'Basculer'}</span>
                    </button>
                  </div>

                  {/* Database Info Box */}
                  <div style={{
                    background: '#0f172a',
                    border: '1px solid #334155',
                    borderRadius: '8px',
                    padding: '10px 12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    fontSize: '0.75rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ color: '#94a3b8', fontWeight: '700' }}>Moteur de données :</span>
                      <span style={{
                        background: tenant.db_type === 'postgres' ? 'rgba(56, 189, 248, 0.15)' : 'rgba(168, 85, 247, 0.15)',
                        color: tenant.db_type === 'postgres' ? '#38bdf8' : '#c084fc',
                        padding: '1px 6px',
                        borderRadius: '4px',
                        fontWeight: '800',
                        fontSize: '0.68rem',
                        textTransform: 'uppercase'
                      }}>
                        {tenant.db_type === 'postgres' ? '🐘 PostgreSQL' : '⚡ PGlite Local'}
                      </span>
                    </div>

                    {tenant.db_type === 'postgres' && (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ color: '#94a3b8' }}>Hôte & Base :</span>
                          <span style={{ color: '#f8fafc', fontWeight: '600' }}>
                            {tenant.host}:{tenant.port} / <b>{tenant.database_name}</b>
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ color: '#94a3b8' }}>Utilisateur :</span>
                          <span style={{ color: '#f8fafc' }}>{tenant.username}</span>
                        </div>
                      </>
                    )}

                    {tenant.db_type === 'pglite' && (
                      <div style={{ color: '#cbd5e1', fontSize: '0.72rem' }}>
                        Base PGlite v17 isolée : <code>loops_tenant_{tenant.id}_pgdata</code>
                      </div>
                    )}
                  </div>

                  {/* Test Result Message */}
                  {testRes && (
                    <div style={{
                      background: testRes.success ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                      border: `1px solid ${testRes.success ? '#22c55e' : '#ef4444'}`,
                      color: testRes.success ? '#4ade80' : '#fca5a5',
                      padding: '8px 10px',
                      borderRadius: '6px',
                      fontSize: '0.72rem'
                    }}>
                      {testRes.message}
                    </div>
                  )}

                  {/* Card Action Buttons */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '8px',
                    borderTop: '1px solid #334155',
                    paddingTop: '12px'
                  }}>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        onClick={() => handleTestTenantDb(tenant)}
                        disabled={testingDbId === tenant.id}
                        style={{
                          background: '#0f172a',
                          border: '1px solid #334155',
                          color: '#38bdf8',
                          borderRadius: '6px',
                          padding: '5px 10px',
                          fontSize: '0.72rem',
                          fontWeight: '700',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <Zap size={13} />
                        <span>{testingDbId === tenant.id ? 'Test...' : 'Tester DB'}</span>
                      </button>

                      <button
                        onClick={() => handleProvisionSchema(tenant.id)}
                        disabled={provisioningId === tenant.id}
                        style={{
                          background: '#0f172a',
                          border: '1px solid #334155',
                          color: '#f8fafc',
                          borderRadius: '6px',
                          padding: '5px 10px',
                          fontSize: '0.72rem',
                          fontWeight: '700',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                        title="Initialiser ou mettre à jour le schéma schema.sql"
                      >
                        <Layers size={13} color="#22c55e" />
                        <span>{provisioningId === tenant.id ? 'En cours...' : 'Init Schéma'}</span>
                      </button>
                    </div>

                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        onClick={() => {
                          setEditingTenant(tenant);
                          setTenantForm({
                            name: tenant.name,
                            slug: tenant.slug,
                            db_type: tenant.db_type,
                            host: tenant.host || 'localhost',
                            port: tenant.port || 5432,
                            database_name: tenant.database_name || 'loops',
                            username: tenant.username || 'postgres',
                            password: '',
                            ssl: tenant.ssl || false,
                            custom_connection_string: tenant.custom_connection_string || '',
                            auto_provision: false
                          });
                          setShowTenantModal(true);
                        }}
                        style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.72rem' }}
                      >
                        <Edit2 size={13} />
                        <span>Éditer</span>
                      </button>

                      {tenant.id !== 1 && (
                        <button
                          onClick={() => {
                            setDeletingTenant(tenant);
                            setDropDbOption(false);
                          }}
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.72rem' }}
                        >
                          <Trash2 size={13} />
                          <span>Supprimer</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 3. TAB 2: UTILISATEURS MULTI-COMPTES */}
      {/* ========================================================================= */}
      {activeTab === 'users' && (
        <div className="database-view-body" style={{ display: 'flex', flexDirection: 'column', gap: '14px', overflowY: 'auto', paddingRight: '4px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', textAlign: 'left' }}>
            <thead>
              <tr style={{ background: '#1e293b', borderBottom: '1px solid #334155', color: '#94a3b8' }}>
                <th style={{ padding: '12px 14px' }}>ID</th>
                <th style={{ padding: '12px 14px' }}>Utilisateur</th>
                <th style={{ padding: '12px 14px' }}>Email</th>
                <th style={{ padding: '12px 14px' }}>Organisation rattachée</th>
                <th style={{ padding: '12px 14px' }}>Rôle</th>
                <th style={{ padding: '12px 14px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} style={{ borderBottom: '1px solid #334155', background: u.role === 'admin' ? 'rgba(56, 189, 248, 0.05)' : 'transparent' }}>
                  <td style={{ padding: '12px 14px', color: '#64748b' }}>#{u.id}</td>
                  <td style={{ padding: '12px 14px', fontWeight: '700', color: '#f8fafc' }}>
                    {u.first_name} {u.last_name}
                  </td>
                  <td style={{ padding: '12px 14px', color: '#38bdf8' }}>{u.email}</td>
                  <td style={{ padding: '12px 14px', color: '#cbd5e1' }}>
                    <span style={{ background: '#0f172a', padding: '3px 8px', borderRadius: '4px', border: '1px solid #334155' }}>
                      {u.tenant_name || `Tenant #${u.tenant_id}`}
                    </span>
                  </td>
                  <td style={{ padding: '12px 14px' }}>
                    <span style={{
                      background: u.role === 'admin' ? 'rgba(56, 189, 248, 0.2)' : 'rgba(100, 116, 139, 0.2)',
                      color: u.role === 'admin' ? '#38bdf8' : '#94a3b8',
                      padding: '2px 8px',
                      borderRadius: '999px',
                      fontSize: '0.70rem',
                      fontWeight: '800',
                      textTransform: 'uppercase'
                    }}>
                      {u.role === 'admin' ? '👑 Admin' : 'Utilisateur'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                    <div style={{ display: 'inline-flex', gap: '8px' }}>
                      <button
                        onClick={() => {
                          setEditingUser(u);
                          setUserForm({
                            tenant_id: u.tenant_id,
                            email: u.email,
                            password: '',
                            first_name: u.first_name || '',
                            last_name: u.last_name || '',
                            role: u.role
                          });
                          setShowUserModal(true);
                        }}
                        style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}
                        title="Modifier l'utilisateur"
                      >
                        <Edit2 size={14} />
                      </button>

                      {u.email !== 'jules@thegreenfuture.ch' && (
                        <button
                          onClick={() => handleDeleteUser(u.id, u.email)}
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}
                          title="Supprimer l'utilisateur"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 4. MODAL: AJOUTER / MODIFIER UNE ORGANISATION & SA BASE */}
      {/* ========================================================================= */}
      {showTenantModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div style={{
            background: '#1e293b',
            border: '1px solid #334155',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '560px',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Building size={20} color="#38bdf8" />
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '800' }}>
                  {editingTenant ? 'Modifier l\'organisation' : 'Nouvelle organisation & base PostgreSQL'}
                </h3>
              </div>
              <button
                onClick={() => setShowTenantModal(false)}
                style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '1.2rem' }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveTenant} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {/* Tenant Name */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '0.75rem', fontWeight: '700', color: '#cbd5e1' }}>Nom de l'entreprise *</label>
                <input
                  type="text"
                  placeholder="ex: Transports Romands SA"
                  value={tenantForm.name}
                  onChange={e => {
                    const val = e.target.value;
                    const autoSlug = val.trim().toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').slice(0, 30);
                    if (!editingTenant) {
                      setTenantForm(prev => ({
                        ...prev,
                        name: val,
                        slug: autoSlug,
                        database_name: autoSlug ? `loops_${autoSlug}` : 'loops_client',
                        username: autoSlug ? `loops_${autoSlug}_user` : 'loops_client_user'
                      }));
                    } else {
                      setTenantForm(prev => ({ ...prev, name: val }));
                    }
                  }}
                  required
                  style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '8px 12px', color: '#fff', fontSize: '0.85rem' }}
                />
              </div>

              {/* DB Type Switcher */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.75rem', fontWeight: '700', color: '#cbd5e1' }}>Moteur de Base de Données</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <button
                    type="button"
                    onClick={() => setTenantForm({ ...tenantForm, db_type: 'postgres' })}
                    style={{
                      background: tenantForm.db_type === 'postgres' ? 'rgba(56, 189, 248, 0.2)' : '#0f172a',
                      border: `1px solid ${tenantForm.db_type === 'postgres' ? '#38bdf8' : '#334155'}`,
                      color: tenantForm.db_type === 'postgres' ? '#38bdf8' : '#94a3b8',
                      borderRadius: '8px',
                      padding: '10px',
                      fontWeight: '700',
                      fontSize: '0.8rem',
                      cursor: 'pointer'
                    }}
                  >
                    🐘 PostgreSQL Standard / Externe
                  </button>

                  <button
                    type="button"
                    onClick={() => setTenantForm({ ...tenantForm, db_type: 'pglite' })}
                    style={{
                      background: tenantForm.db_type === 'pglite' ? 'rgba(168, 85, 247, 0.2)' : '#0f172a',
                      border: `1px solid ${tenantForm.db_type === 'pglite' ? '#c084fc' : '#334155'}`,
                      color: tenantForm.db_type === 'pglite' ? '#c084fc' : '#94a3b8',
                      borderRadius: '8px',
                      padding: '10px',
                      fontWeight: '700',
                      fontSize: '0.8rem',
                      cursor: 'pointer'
                    }}
                  >
                    ⚡ PGlite Embarqué Isolé
                  </button>
                </div>
              </div>

              {/* Postgres Specific Settings */}
              {tenantForm.db_type === 'postgres' && (
                <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '10px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: '700' }}>Host PostgreSQL</label>
                      <input
                        type="text"
                        placeholder="localhost ou rds.amazonaws.com"
                        value={tenantForm.host}
                        onChange={e => setTenantForm({ ...tenantForm, host: e.target.value })}
                        style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '6px', padding: '6px 10px', color: '#fff', fontSize: '0.82rem' }}
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: '700' }}>Port</label>
                      <input
                        type="number"
                        value={tenantForm.port}
                        onChange={e => setTenantForm({ ...tenantForm, port: e.target.value })}
                        style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '6px', padding: '6px 10px', color: '#fff', fontSize: '0.82rem' }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: '700' }}>Nom de la Base (Database) *</label>
                    <input
                      type="text"
                      placeholder="ex: loops_client1"
                      value={tenantForm.database_name}
                      onChange={e => setTenantForm({ ...tenantForm, database_name: e.target.value })}
                      required
                      style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '6px', padding: '6px 10px', color: '#fff', fontSize: '0.82rem' }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: '700' }}>Utilisateur</label>
                      <input
                        type="text"
                        value={tenantForm.username}
                        onChange={e => setTenantForm({ ...tenantForm, username: e.target.value })}
                        style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '6px', padding: '6px 10px', color: '#fff', fontSize: '0.82rem' }}
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: '700' }}>Mot de passe</label>
                      <input
                        type="password"
                        placeholder="••••••••"
                        value={tenantForm.password}
                        onChange={e => setTenantForm({ ...tenantForm, password: e.target.value })}
                        style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '6px', padding: '6px 10px', color: '#fff', fontSize: '0.82rem' }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Initial User Creation for New Tenant */}
              {!editingTenant && (
                <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '10px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#38bdf8' }}>
                    👤 Créer un compte administrateur pour ce client (Optionnel)
                  </span>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <input
                      type="email"
                      placeholder="Email du client"
                      value={tenantForm.initial_user_email}
                      onChange={e => setTenantForm({ ...tenantForm, initial_user_email: e.target.value })}
                      style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '6px', padding: '6px 10px', color: '#fff', fontSize: '0.82rem' }}
                    />
                    <input
                      type="password"
                      placeholder="Mot de passe temporaire"
                      value={tenantForm.initial_user_password}
                      onChange={e => setTenantForm({ ...tenantForm, initial_user_password: e.target.value })}
                      style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '6px', padding: '6px 10px', color: '#fff', fontSize: '0.82rem' }}
                    />
                  </div>
                </div>
              )}

              {/* Modal Buttons */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                <button
                  type="button"
                  onClick={() => setShowTenantModal(false)}
                  style={{ background: '#334155', color: '#cbd5e1', border: 'none', borderRadius: '6px', padding: '8px 14px', fontSize: '0.8rem', fontWeight: '700', cursor: 'pointer' }}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  style={{ padding: '8px 18px', fontSize: '0.82rem' }}
                >
                  {editingTenant ? 'Enregistrer les modifications' : 'Créer l\'organisation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 5. MODAL: AJOUTER / MODIFIER UN UTILISATEUR */}
      {/* ========================================================================= */}
      {showUserModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div style={{
            background: '#1e293b',
            border: '1px solid #334155',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '460px',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.5)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Users size={20} color="#38bdf8" />
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '800' }}>
                  {editingUser ? 'Modifier l\'utilisateur' : 'Créer un compte utilisateur'}
                </h3>
              </div>
              <button
                onClick={() => setShowUserModal(false)}
                style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '1.2rem' }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveUser} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Tenant Selection */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '0.75rem', fontWeight: '700', color: '#cbd5e1' }}>Organisation rattachée *</label>
                <select
                  value={userForm.tenant_id}
                  onChange={e => setUserForm({ ...userForm, tenant_id: e.target.value })}
                  required
                  style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '8px 10px', color: '#fff', fontSize: '0.82rem' }}
                >
                  <option value="">-- Sélectionner une organisation --</option>
                  {tenants.map(t => (
                    <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>
                  ))}
                </select>
              </div>

              {/* Email */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '0.75rem', fontWeight: '700', color: '#cbd5e1' }}>Adresse Email *</label>
                <input
                  type="email"
                  value={userForm.email}
                  onChange={e => setUserForm({ ...userForm, email: e.target.value })}
                  required
                  style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '8px 10px', color: '#fff', fontSize: '0.82rem' }}
                />
              </div>

              {/* Names */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '0.75rem', fontWeight: '700', color: '#cbd5e1' }}>Prénom</label>
                  <input
                    type="text"
                    value={userForm.first_name}
                    onChange={e => setUserForm({ ...userForm, first_name: e.target.value })}
                    style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '8px 10px', color: '#fff', fontSize: '0.82rem' }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '0.75rem', fontWeight: '700', color: '#cbd5e1' }}>Nom</label>
                  <input
                    type="text"
                    value={userForm.last_name}
                    onChange={e => setUserForm({ ...userForm, last_name: e.target.value })}
                    style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '8px 10px', color: '#fff', fontSize: '0.82rem' }}
                  />
                </div>
              </div>

              {/* Password */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '0.75rem', fontWeight: '700', color: '#cbd5e1' }}>
                  {editingUser ? 'Nouveau mot de passe (laisser vide pour ne pas changer)' : 'Mot de passe initial *'}
                </label>
                <input
                  type="password"
                  value={userForm.password}
                  onChange={e => setUserForm({ ...userForm, password: e.target.value })}
                  required={!editingUser}
                  style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '8px 10px', color: '#fff', fontSize: '0.82rem' }}
                />
              </div>

              {/* Role */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '0.75rem', fontWeight: '700', color: '#cbd5e1' }}>Rôle</label>
                <select
                  value={userForm.role}
                  onChange={e => setUserForm({ ...userForm, role: e.target.value })}
                  style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '8px 10px', color: '#fff', fontSize: '0.82rem' }}
                >
                  <option value="user">Utilisateur Standard (Accès à son organisation uniquement)</option>
                  <option value="admin">👑 Super Administrateur (Gestion globale)</option>
                </select>
              </div>

              {/* Modal Buttons */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
                <button
                  type="button"
                  onClick={() => setShowUserModal(false)}
                  style={{ background: '#334155', color: '#cbd5e1', border: 'none', borderRadius: '6px', padding: '8px 14px', fontSize: '0.8rem', fontWeight: '700', cursor: 'pointer' }}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  style={{ padding: '8px 18px', fontSize: '0.82rem' }}
                >
                  {editingUser ? 'Mettre à jour' : 'Créer l\'utilisateur'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DELETE TENANT MODAL */}
      {deletingTenant && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.75)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
        >
          <div
            className="overlay-card"
            style={{
              width: '100%',
              maxWidth: '480px',
              padding: '24px',
              borderRadius: '12px',
              border: '1px solid #ef4444',
              background: '#0f172a',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{ background: 'rgba(239, 68, 68, 0.2)', padding: '10px', borderRadius: '10px' }}>
                <Trash2 size={24} color="#ef4444" />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '800', color: '#f8fafc' }}>
                  Supprimer l'organisation
                </h3>
                <div style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                  {deletingTenant.name} ({deletingTenant.slug})
                </div>
              </div>
            </div>

            <p style={{ color: '#cbd5e1', fontSize: '0.85rem', lineHeight: '1.5', margin: '0 0 16px 0' }}>
              Êtes-vous sûr de vouloir supprimer définitivement l'organisation <strong>« {deletingTenant.name} »</strong> et l'ensemble des comptes utilisateurs associés ?
            </p>

            {deletingTenant.db_type === 'postgres' && (
              <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', padding: '12px', marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={dropDbOption}
                    onChange={e => setDropDbOption(e.target.checked)}
                    style={{ width: '18px', height: '18px', marginTop: '2px', accentColor: '#ef4444' }}
                  />
                  <div>
                    <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#fca5a5' }}>
                      Supprimer également la base physique PostgreSQL (DROP DATABASE)
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '2px' }}>
                      Base ciblée : <code style={{ color: '#38bdf8' }}>{deletingTenant.database_name || 'loops'}</code> sur <code style={{ color: '#38bdf8' }}>{deletingTenant.host || 'localhost'}</code>.
                      Si non cochée, la base restera sur votre serveur PostgreSQL.
                    </div>
                  </div>
                </label>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                onClick={() => setDeletingTenant(null)}
                disabled={isDeletingTenant}
                style={{
                  background: '#1e293b',
                  border: '1px solid #334155',
                  color: '#94a3b8',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '0.85rem',
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
              >
                Annuler
              </button>

              <button
                type="button"
                onClick={handleConfirmDeleteTenant}
                disabled={isDeletingTenant}
                style={{
                  background: '#ef4444',
                  border: 'none',
                  color: '#ffffff',
                  padding: '8px 18px',
                  borderRadius: '6px',
                  fontSize: '0.85rem',
                  fontWeight: '800',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {isDeletingTenant ? <RefreshCw size={14} className="spin" /> : <Trash2 size={14} />}
                <span>Confirmer la suppression</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
